import { Box, Grid, Typography, useTheme, IconButton } from "@mui/material"
import Header from "components/Header/Header"
import AppButton from "components/StyledComponents/AppButton"
import { createBreadcrumbs } from "helpers/breadCrumbs"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import styles from "./notable.module.scss"
import {
  getVipGroupDynamicFieldsAction,
  deleteVipGroupSectionAction,
  getNotableGroupTypesAction,
  deleteVipGroupFieldAction,
  getVipGroupFieldsAction
} from "redux/superAdmin/actions"
import { Create } from "@mui/icons-material"
import trashIcon from "assets/icons/trash.svg"
import SectionModal from "./components/SectionModal"
import FieldModal from "./components/FieldModal"
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog"

const NotableGroupManagement = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const [breadCrumbs, setBreadCrumbs] = useState([])
  const { menuFields, schemaFields } = useSelector((store) => store?.superAdmin || [])

  const [selectedSection, setSelectedSection] = useState(null)
  const [openSectionModal, setOpenSectionModal] = useState(false)
  const [openFieldModal, setOpenFieldModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [modalData, setModalData] = useState(null)
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false)
  const [deleteData, setDeleteData] = useState(null)

  useEffect(() => {
    document.title = "Notable Groups Management"
    setBreadCrumbs(createBreadcrumbs())
    dispatch(getVipGroupDynamicFieldsAction())
    dispatch(getNotableGroupTypesAction())
  }, [])

  useEffect(() => {
    if (selectedSection) {
      dispatch(getVipGroupFieldsAction(selectedSection.id))
    }
  }, [selectedSection])

  useEffect(() => {
    if (menuFields?.length > 0 && !selectedSection) {
      setSelectedSection(menuFields[0])
    }
  }, [menuFields])

  const handleOpenSectionModal = (data = null) => {
    setIsEdit(!!data)
    setModalData(data)
    setOpenSectionModal(true)
  }

  const handleOpenFieldModal = (data = null) => {
    setIsEdit(!!data)
    setModalData(data)
    setOpenFieldModal(true)
  }

  const handleDeleteConfirm = (type, data) => {
    setDeleteData({ type, data })
    setOpenDeleteConfirm(true)
  }

  const handleDelete = () => {
    if (deleteData.type === "section") {
      dispatch(deleteVipGroupSectionAction({ id: deleteData.data.id }))
      if (selectedSection?.id === deleteData.data.id) {
        setSelectedSection(null)
      }
    } else {
      dispatch(deleteVipGroupFieldAction({ id: deleteData.data.id, parentId: selectedSection }))
    }
    setOpenDeleteConfirm(false)
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Header title="Notable Groups Management" breadCrumbs={breadCrumbs} />
      </Grid>

      <Grid item xs={12} md={4}>
        <Box className={styles.arrowMenu}>
          <Box className={styles.arrowMenuContent}>
            <Box className="d-flex justify-content-between align-items-center mb-4">
              <Typography variant="h3" color="white">
                Sections
              </Typography>
              <AppButton
                onClick={() => handleOpenSectionModal()}
                styles={{ padding: "8px 16px", fontSize: "12px" }}>
                Add Section
              </AppButton>
            </Box>

            {menuFields?.map((section, index) => (
              <Box
                key={section.id - index}
                className={`${styles.category_grid} d-flex justify-content-between`}
                sx={{
                  backgroundColor:
                    selectedSection?.id === section.id
                      ? theme.palette.background.default
                      : "transparent",
                  borderColor:
                    selectedSection?.id === section.id ? theme.palette.secondary.main : "white"
                }}
                onClick={() => setSelectedSection(section)}>
                <Box className="d-flex align-items-center">
                  <Typography variant="body1Regular" color="white">
                    {section.field_label}
                  </Typography>
                </Box>
                <Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleOpenSectionModal(section)
                    }}
                    sx={{ color: "white" }}>
                    <Create fontSize="small" htmlColor={theme.palette.white.main} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteConfirm("section", section)
                    }}
                    sx={{ color: "white" }}>
                    <img src={trashIcon} alt="delete" height={18} />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={8} sx={{ position: "relative" }}>
        <Box className={styles.arrow} />

        <Box
          sx={{
            background: theme.palette.primary.main,
            borderRadius: "12px",
            padding: "32px",
            marginLeft: "34px",
            minHeight: "80vh"
          }}>
          {selectedSection ? (
            <>
              <Box className="d-flex justify-content-between align-items-center mb-4">
                <Typography variant="h3" color={theme.palette.text.primary}>
                  Fields for {selectedSection.field_label}
                </Typography>
                <AppButton
                  onClick={() => handleOpenFieldModal()}
                  styles={{ padding: "8px 16px", fontSize: "12px" }}>
                  Add Field
                </AppButton>
              </Box>

              {schemaFields?.length > 0 ? (
                <Grid container spacing={2}>
                  {schemaFields.map((field) => (
                    <Grid item xs={12} key={field.id} className={styles.itemLabel}>
                      <Box className="d-flex justify-content-between align-items-center p-0">
                        <Box>
                          <Typography variant="body1Bold" color={theme.palette.text.primary}>
                            {field.schema_field_label}
                          </Typography>
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteConfirm("field", field)}>
                            <img src={trashIcon} alt="delete" height={18} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box className="d-flex justify-content-center align-items-center mt-5">
                  <Typography variant="body1Regular" color={theme.palette.text.secondary}>
                    No fields added to this section yet.
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box className="d-flex justify-content-center align-items-center h-100">
              <Typography variant="h4" color={theme.palette.text.secondary}>
                Select a section to manage its fields
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>
      <SectionModal
        open={openSectionModal}
        onClose={() => setOpenSectionModal(false)}
        isEdit={isEdit}
        sectionData={modalData}
      />

      <FieldModal
        open={openFieldModal}
        onClose={() => setOpenFieldModal(false)}
        fieldData={modalData}
        section={selectedSection}
      />

      <DeleteConfirmationDialog
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        onDelete={handleDelete}
        title={`Delete ${deleteData?.type}`}
        description={`Are you sure you want to delete this ${deleteData?.type}? This action cannot be undone.`}
      />
    </Grid>
  )
}

export default NotableGroupManagement
